import { DecimalPipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core'
import { FormControl, FormsModule } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { NgxScannerQrcodeComponent, ScannerQRCodeConfig, ScannerQRCodeResult } from 'ngx-scanner-qrcode'
import { Observable } from 'rxjs'
import { map, startWith } from 'rxjs/operators'
import { CameraService } from 'src/app/shared/camera-service'

import { BaseComponent, FuelEntry, ListItem } from '../../shared'

@Component({
    templateUrl: './fuel-entry.component.html',
    styleUrls: ['./fuel-entry.component.css'],
    standalone: true,
    imports: [FormsModule, WjInputModule, MatButton, FaIconComponent, DecimalPipe, NgxScannerQrcodeComponent],
})
export class FuelEntryComponent extends BaseComponent implements OnDestroy, OnInit {
    @ViewChild('action') action!: NgxScannerQrcodeComponent
    private cameraService = inject(CameraService)

    myControl = new FormControl()
    faSpinner = faSpinner
    source = ''
    vehicle: string | undefined = ''
    gallons: number | null = null
    lifetimeMeter = 0
    lifetimeMeterId = ''
    meter: number | null = null
    date = new Date()
    isSaving = false
    saved = false
    fuelSourceOptions: ListItem<string>[] = []
    fuelSources: any[] = []
    fuelVehicleOptions: ListItem<string>[] = []
    fuelVehicles: any[] = []
    allEquipment: any[] = []
    jobs: any[] = []
    jobOptions: ListItem<string>[] = []
    job!: string
    filteredOptions!: Observable<any[]>
    lifetimeMeterRequired = false
    validationMessage = ''
    transportLastFillup = 0

    get isCollapsed(): boolean {
        if (this.source && this.vehicle) return true
        return false
    }
    get fuelCapacity(): number {
        if (this.vehicle) {
            const match = this.allEquipment.filter(x => x.equipment === this.vehicle)[0]
            if (match) {
                return match.fuelCapacity
            }
        }
        return 0
    }
    get lifetimeMeterIdHidden(): boolean {
        if (this.lifetimeMeterRequired && this.source.startsWith('FT')) return false
        return true
    }
    get tranportInversed() {
        if (this.vehicle && this.vehicle.startsWith('TI')) return this.vehicle + '/' + this.vehicle.replace('TI', 'TO')
        return ''
    }
    config: ScannerQRCodeConfig = {
        vibrate: 400,
        isBeep: false,
        constraints: {
            audio: false,
        },
    }

    constructor() {
        super()
        this.refresh()
    }

    async ngOnInit() {
        const cameras = await this.cameraService.getCameras()
        const hasEnvironmentCamera = cameras.some(camera => camera.facingMode === 'environment')
        if (this.config?.constraints?.video) {
            this.config.constraints.video = hasEnvironmentCamera
                ? { facingMode: { exact: 'environment' } }
                : { facingMode: { exact: 'user' } }
        }
    }

    ngOnDestroy() {
        if (this.action) this.action.stop()
    }
    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.get('api/VpEquipment'), this.services.data.vpJobs.queryAll()).subscribe({
                next: result => {
                    const items = (result[0] as any[]).orderBy(x => x.equipment)
                    this.allEquipment = result[0]
                    const fuelSources = [] as any[]
                    const fuelVehicles = [] as any[]

                    for (const item of items) {
                        if (item.udFueler === 'Y') {
                            this.fuelSources.push(item)
                            fuelSources.push(new ListItem(item.equipment, item.equipment + ': ' + item.description))
                        } else if (item.fuelTypeIn !== 'N' && item.fuelTypeIn !== 'G' && item.equipment[0] !== 'P') {
                            this.fuelVehicles.push(item)
                            fuelVehicles.push(new ListItem(item.equipment, item.equipment + ': ' + item.description))
                        }
                    }

                    this.fuelSourceOptions = fuelSources
                    this.fuelVehicleOptions = fuelVehicles
                    this.jobs = this.services.data.vpJobs.items
                    this.jobOptions = this.jobs.map(x => new ListItem(x.id, x.id + ' ' + x.name))
                    // Now that we have the jobs data, we can initialize the filter the autocomplete will use.
                    this.filteredOptions = this.myControl.valueChanges.pipe(
                        startWith(''),
                        map(value => {
                            const name = typeof value === 'string' ? value : value?.id
                            return name ? this._filter(name as string) : this.jobs.slice()
                        }),
                    )

                    // Finally, start the camera.
                    this.action.start()
                    this.reset()
                    this.initialLoadingComplete()
                },
            }),
        )
    }
    onError(e: any): void {
        alert(e)
    }
    onScan(e: ScannerQRCodeResult[]) {
        if (!e[0]) {
            return
        }
        const val = e[0].value
        if (this.fuelSources.filter(x => x.equipment === val).length > 0) {
            if (!this.source) {
                this.source = val
                this.services.notification.success('Fuel OUT Entered', undefined, true)
            }
            return
        }
        if (this.fuelVehicles.filter(x => x.equipment === val).length > 0) {
            if (!this.vehicle) {
                this.vehicle = val
                this.services.notification.success('Fuel IN Entered', undefined, true)
            }
            return
        }
        if (this.vehicle && this.source) {
            this.action.pause()
            return
        }
        this.services.notification.warning('QR Code Not Recognized', undefined, true)
    }
    reset() {
        this.vehicle = ''
        this.source = ''
        this.job = ''
        this.gallons = null
        this.meter = null
        this.lifetimeMeter = 0
        this.action.start()
        this.action.play()
        this.saved = false
        this.validationMessage = ''
        this.lifetimeMeterRequired = false
        this.lifetimeMeterId = ''
        this.date = new Date()
        this.transportLastFillup = 0
        setTimeout(() => {
            this.vehicle = ''
            this.source = ''
            this.job = ''
        }, 10)
    }
    save() {
        this.isSaving = true
        const entry = new FuelEntry()
        entry.gallons = this.gallons ? this.gallons : 0
        entry.meter = this.meter ? Math.round(this.meter) : 0
        entry.source = this.source
        entry.vehicle = this.vehicle ?? ''
        entry.jobId = this.job
        entry.validationMessage = this.validationMessage
        entry.date = this.date
        entry.lifetimeMeter = this.lifetimeMeter
        entry.lifetimeMeterId = this.lifetimeMeterId

        if (this.lifetimeMeterRequired && this.lifetimeMeter === 0) {
            this.validationMessage = 'Lifetime Meter is required once per day per Fuel Farm or Fuel Transfer Tank'
            this.isSaving = false
            return
        }

        if (this.validationMessage) {
            this.cancelableRequest(
                '',
                true,
                this.services.data.load(true, true, this.services.data.fuelEntries.add(entry)).subscribe({
                    next: () => {
                        this.saved = true
                        this.isSaving = false
                        this.action.stop()
                    },
                    error: () => {
                        this.isSaving = false
                        this.services.notification.error('There was a problem saving the fuel entry.')
                    },
                }),
            )
        } else {
            this.cancelableRequest(
                'validation',
                true,
                this.services.data.load(true, true, this.services.data.post('/api/FuelEntry/PreSaveValidation', entry)).subscribe({
                    next: result => {
                        if (result[0].validationMessage) {
                            this.validationMessage = result[0].validationMessage
                            this.isSaving = false
                        } else {
                            this.cancelableRequest(
                                'saveEntry',
                                true,
                                this.services.data.load(true, true, this.services.data.fuelEntries.add(entry)).subscribe({
                                    next: () => {
                                        this.saved = true
                                        this.isSaving = false
                                        this.action.stop()
                                    },
                                    error: () => {
                                        this.isSaving = false
                                        this.services.notification.error('There was a problem saving the fuel entry.')
                                    },
                                }),
                            )
                        }
                    },
                    error: () => {
                        this.isSaving = false
                        this.services.notification.error('There was a problem saving the fuel entry.')
                    },
                }),
            )
        }
    }
    onSourceChanged() {
        // Clear validationMessage whenever the source is changed. This is separate from the query below it.
        this.validationMessage = ''

        this.lifetimeMeterRequired = false
        if (!this.source || this.isInitialLoading) return
        if (!this.source.startsWith('FT') && !this.source.startsWith('TO')) return

        this.cancelableRequest(
            'lifetimeCheck',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get(
                        'api/FuelEntry/IsLifetimeMeterRequired',
                        new HttpParams().set('source', this.source).set('destination', this.vehicle ?? ''),
                    ),
                )
                .subscribe({
                    next: result => {
                        this.lifetimeMeterRequired = result[0]
                    },
                }),
        )
    }
    onVehicleChanged() {
        // Clear validationMessage whenever the source is changed. This is separate from the query below it.
        this.validationMessage = ''
        this.transportLastFillup = 0
        if (!this.vehicle) return
        if (!this.vehicle.startsWith('TI')) return

        this.cancelableRequest(
            'transportLastFillup',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('api/FuelEntry/TransportLastFillup', new HttpParams().set('destination', this.vehicle)),
                )
                .subscribe({
                    next: result => {
                        this.transportLastFillup = result[0]
                    },
                }),
        )
    }
    onLifetimeChanged() {
        if (this.lifetimeMeter > 0) this.validationMessage = ''
    }
    displayFn(job: any): string {
        return job
    }
    private _filter(id: string): any[] {
        const filterValue = id.toLowerCase()
        return this.jobs.filter(option => option.id.toLowerCase().includes(filterValue))
    }
}
